import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  IconButton,
  Typography,
  CircularProgress,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';

const INITIAL_MESSAGE = {
  text: "👋 Hello! I'm your resume assistant. Ask me anything about the resumes and candidates, and I'll help you find the information you need!",
  isUser: false,
  timestamp: new Date().toLocaleTimeString()
};

const Chatbot = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([INITIAL_MESSAGE]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleToggle = () => {
    setOpen(!open);
  };

  const handleMessageChange = (event) => {
    setNewMessage(event.target.value);
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;

    const userMessage = {
      text: newMessage,
      isUser: true,
      timestamp: new Date().toLocaleTimeString()
    };

    setMessages(prev => [...prev, userMessage]);
    setNewMessage('');
    setLoading(true);

    try {
      const response = await fetch('https://api-resumex.experionglobal.dev/nlp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ message: userMessage.text })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data && data.data && data.data.result) {
        const botMessage = {
          text: data.data.result,
          isUser: false,
          timestamp: new Date().toLocaleTimeString()
        };
        setMessages(prev => [...prev, botMessage]);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = {
        text: "Sorry, I encountered an error processing your request. Please try again.",
        isUser: false,
        timestamp: new Date().toLocaleTimeString()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  if (!open) {
    return (
      <Box
        sx={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          zIndex: 1000,
        }}
      >
        <IconButton
          onClick={handleToggle}
          sx={{
            backgroundColor: '#dc143c',
            color: '#ffffff',
            '&:hover': {
              backgroundColor: '#b01030',
            },
            width: 56,
            height: 56,
            boxShadow: '0 4px 12px rgba(220, 20, 60, 0.2)',
          }}
        >
          <ChatBubbleOutlineIcon />
        </IconButton>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: '20px',
        right: '20px',
        width: '350px',
        height: '500px',
        zIndex: 1000,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(10px)',
          borderRadius: 3,
          border: '1px solid rgba(220, 20, 60, 0.1)',
          boxShadow: '0 8px 32px rgba(220, 20, 60, 0.15)',
        }}
      >
        <Box
          sx={{
            p: 2,
            backgroundColor: '#dc143c',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12,
          }}
        >
          <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 600 }}>
            Resume Assistant
          </Typography>
          <IconButton
            size="small"
            onClick={handleToggle}
            sx={{ color: '#ffffff' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          {messages.map((message, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                justifyContent: message.isUser ? 'flex-end' : 'flex-start',
                mb: 1,
              }}
            >
              <Box
                sx={{
                  maxWidth: '80%',
                  backgroundColor: message.isUser ? '#dc143c' : '#ffffff',
                  color: message.isUser ? '#ffffff' : '#3a3532',
                  borderRadius: 2,
                  p: 1.5,
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                }}
              >
                <Typography variant="body1" style={{ whiteSpace: 'pre-wrap' }}>
                  {message.text}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.7, mt: 0.5, display: 'block' }}>
                  {message.timestamp}
                </Typography>
              </Box>
            </Box>
          ))}
          <div ref={messagesEndRef} />
        </Box>

        <Box
          sx={{
            p: 2,
            backgroundColor: '#ffffff',
            borderTop: '1px solid rgba(220, 20, 60, 0.1)',
            display: 'flex',
            gap: 1,
            borderBottomLeftRadius: 12,
            borderBottomRightRadius: 12,
          }}
        >
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Type your message..."
            value={newMessage}
            onChange={handleMessageChange}
            onKeyPress={handleKeyPress}
            size="small"
            disabled={loading}
            sx={{
              backgroundColor: 'white',
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#dc143c',
                },
              },
            }}
          />
          <IconButton
            onClick={handleSendMessage}
            disabled={loading || !newMessage.trim()}
            sx={{
              backgroundColor: !loading && newMessage.trim() ? '#dc143c' : 'transparent',
              color: !loading && newMessage.trim() ? '#ffffff' : '#3a3532',
              '&:hover': {
                backgroundColor: !loading && newMessage.trim() ? '#b01030' : undefined,
              },
            }}
          >
            {loading ? <CircularProgress size={24} /> : <SendIcon />}
          </IconButton>
        </Box>
      </Paper>
    </Box>
  );
};

export default Chatbot;
